/* eslint-disable react/prop-types */
import { Box, Button as MuiButton } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Formik } from "formik";
import { omit } from "lodash/fp";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import Dropdown from "../../../../components/common/new/Dropdown/Dropdown";
import InputField from "../../../../components/common/new/InputField/InputField";
import { Appointment, ChiefSIToIdMapper } from "../../../../contants";
import { APPOINTMENT_MEMBER_SEARCH } from "../../../../contants/formFields";
import {
  SET_APPOINTMENT_FORM_STATE,
  SET_POSITION_ID,
} from "../../../../redux/actionTypes/dashboard";
import {
  selectUserProfile,
  selectUserRole,
} from "../../../../redux/selectors/user";
import IntroductionForm from "../../../../services/api/introductionForm";
import { getDropdownOptions } from "../../../../utils";
import AppointmentPageTopInfo from "../../AppointmentPageTopInfo";
import {
  NextChiefApiMapper,
  NextChiefNameMapper,
} from "../../../../utils/helper";

const SKIP_INPUTS = [
  APPOINTMENT_MEMBER_SEARCH.APPLICATION_TYPE,
  APPOINTMENT_MEMBER_SEARCH.APPLICATION_FOR,
];

const prepareFormData = (formValues, chiefLabel) => {
  const sanitizeFormValues = omit(SKIP_INPUTS, formValues);

  return Object.keys(sanitizeFormValues).reduce((acc, key) => {
    let result = { [key]: sanitizeFormValues[key] };

    if (key === APPOINTMENT_MEMBER_SEARCH.CHEIF_SI) {
      const newKey = `${chiefLabel.toLowerCase()}Id`;
      result = { [newKey]: sanitizeFormValues[key] };
    }
    return Object.assign(acc, result);
  }, {});
};

// MD WD YMD YWD

const DIVISION_OPTIONS = [
  {
    value: 1,
    key: "MD",
  },
  {
    value: 2,
    key: "WD",
  },
  {
    value: 37,
    key: "YMD",
  },
  {
    value: 3,
    key: "YWD",
  },
];
const SEARCH_TYPE_OPTIONS = [
  { value: 0, key: "Before 30-Apr-26" },
  { value: 1, key: "After 01-May-26" },
];

function MemberSearchForm({ onSubmit }) {
  const [cheifSiOptions, setChiefSiOptions] = useState([]);
  const [positionsOptions, setPositionsOptions] = useState([]);
  const userRole = useSelector(selectUserRole);
  const userData = useSelector(selectUserProfile);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const selectChiefLabel = NextChiefNameMapper[userRole];

  const getNextChiefApiAndPayload = () => {
    const api = NextChiefApiMapper[userRole];
    const key = ChiefSIToIdMapper[userRole];
    const payload = { [key]: userData[key] };

    return {
      api,
      payload,
    };
  };

  const filterPositions = (options) => {
    const positions = options.filter(
      (opt) => opt.structureLevel === selectChiefLabel?.toUpperCase()
    );
    return getDropdownOptions(positions, "name", "positionId");
  };

  const fetchOptions = async () => {
    try {
      const { api, payload } = getNextChiefApiAndPayload();

      const { data } = await api({ data: payload });
      setChiefSiOptions(getDropdownOptions(data, "name", "structureId"));
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const fetchPositions = async () => {
    try {
      const { data } = await IntroductionForm.getAllPositions(
        Appointment.MAINLINE_POSITION
      );
      setPositionsOptions(filterPositions(data));
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchPositions();
  }, []);

  return (
    <Box>
      <AppointmentPageTopInfo />
      <Formik
        initialValues={{ searchType: 1 }}
        onSubmit={(values) => {
          const formValues = prepareFormData(values, selectChiefLabel);
          dispatch({
            type: SET_APPOINTMENT_FORM_STATE,
            payload: formValues,
            cheifSiOptions,
          });
          onSubmit(formValues);
        }}
      >
        {({ values, handleChange, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "center", md: "flex-start" },
                gap: { xs: 2, md: 1 },
                width: "100%",
                ...(isMobile && {
                  "& > *": {
                    width: "70% !important",
                    maxWidth: "300px",
                  },
                }),
              }}
            >
              {/* Chief SI Dropdown */}
              <Box
                sx={{ flex: { xs: "none", md: 1 }, maxWidth: { md: "22%" } }}
              >
                <Dropdown
                  inputProps={{ required: true }}
                  name={APPOINTMENT_MEMBER_SEARCH.CHEIF_SI}
                  value={values[APPOINTMENT_MEMBER_SEARCH.CHEIF_SI]}
                  options={cheifSiOptions}
                  label={`Select ${selectChiefLabel}`}
                  onChange={handleChange}
                  showCross
                  handleClearValue={() =>
                    setFieldValue(APPOINTMENT_MEMBER_SEARCH.CHEIF_SI, "")
                  }
                />
              </Box>

              {/* Division Dropdown */}
              <Box
                sx={{ flex: { xs: "none", md: 1 }, maxWidth: { md: "22%" } }}
              >
                <Dropdown
                  name={APPOINTMENT_MEMBER_SEARCH.DIVISION}
                  value={values[APPOINTMENT_MEMBER_SEARCH.DIVISION]}
                  options={DIVISION_OPTIONS}
                  label="Select Division"
                  onChange={handleChange}
                  showCross
                  handleClearValue={() =>
                    setFieldValue(APPOINTMENT_MEMBER_SEARCH.DIVISION, "")
                  }
                  inputProps={{ required: true }}
                />
              </Box>

              {/* Application For Dropdown */}
              <Box
                sx={{ flex: { xs: "none", md: 1 }, maxWidth: { md: "22%" } }}
              >
                <Dropdown
                  inputProps={{ required: true }}
                  name={APPOINTMENT_MEMBER_SEARCH.APPLICATION_FOR}
                  value={values[APPOINTMENT_MEMBER_SEARCH.APPLICATION_FOR]}
                  options={positionsOptions}
                  label="Application For"
                  showCross
                  onChange={(e) => {
                    dispatch({
                      type: SET_POSITION_ID,
                      payload: e.target.value,
                    });
                    handleChange(e);
                  }}
                  handleClearValue={() =>
                    setFieldValue(APPOINTMENT_MEMBER_SEARCH.APPLICATION_FOR, "")
                  }
                />
              </Box>

              {/* Member Name/ID Input */}
              <Box
                sx={{ flex: { xs: "none", md: 1 }, maxWidth: { md: "22%" } }}
              >
                <InputField
                  name={APPOINTMENT_MEMBER_SEARCH.SEARCH_MEMBER}
                  value={values[APPOINTMENT_MEMBER_SEARCH.SEARCH_MEMBER]}
                  label="Member Name/ID"
                  onChange={handleChange}
                />
              </Box>
              {/* NEW Search Type */}
              <Box sx={{ flex: 1, maxWidth: { md: "16%" } }}>
                <Dropdown
                  name="searchType"
                  value={values.searchType}
                  options={SEARCH_TYPE_OPTIONS}
                  label="Search Type"
                  onChange={handleChange}
                  inputProps={{ required: true }}
                />
              </Box>

              {/* Search Button */}
              <Box
                sx={{
                  flex: { xs: "none", md: "none" },
                  width: { xs: "70%", md: "auto" },
                  maxWidth: { xs: "300px" },
                  marginLeft: { xs: "", md: "15px" },
                }}
              >
                <MuiButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  Search
                </MuiButton>
              </Box>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
}

export default MemberSearchForm;
